import { createFileRoute, redirect } from "@tanstack/react-router";
import { logTikTokVisit } from "@/lib/tiktok-analytics.functions";

/**
 * TikTok CTA tracking entry point.
 *
 * The burnt-in call to action and the scan-me QR code in every Pal Stars
 * TikTok point at "palstars.com/tiktok". This route logs the visit for the
 * admin analytics dashboard, then forwards to the home page with UTM
 * parameters so Google Analytics (G-CR7QGT89CB) also attributes the session to
 * the TikTok campaign. `?v=` carries the individual video slug
 * (e.g. /tiktok?v=palsino).
 */
export const Route = createFileRoute("/tiktok")({
  beforeLoad: ({ search }) => {
    const v = typeof (search as Record<string, unknown>)?.v === "string"
      ? String((search as Record<string, string>).v)
      : "general";
    const content = v.replace(/[^a-z0-9-]/gi, "").slice(0, 40) || "general";

    if (typeof window !== "undefined") {
      void logTikTokVisit({
        data: {
          video: content,
          utmSource: "tiktok",
          utmMedium: "social",
          referrerHost: (() => {
            try {
              return document.referrer ? new URL(document.referrer).host : undefined;
            } catch {
              return undefined;
            }
          })(),
          device: window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop",
        },
      }).catch(() => {});
    }

    throw redirect({
      href: `/?utm_source=tiktok&utm_medium=social&utm_campaign=palstars-tiktok&utm_content=${content}`,
    });
  },
  head: () => ({
    meta: [
      { title: "Pal Stars on TikTok — Palworld Wiki, TCG Packs & Palsino" },
      {
        name: "description",
        content:
          "You came from a Pal Stars TikTok. Jump into the free Palworld wiki, TCG card database, booster pack simulator and Palsino slot machine.",
      },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "Pal Stars on TikTok — Palworld Wiki & TCG" },
      {
        property: "og:description",
        content: "Free Palworld wiki, card database, booster packs and Palsino spins.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
});
